import type { ReactNode } from 'react';
import { useNotificationPreferences, usePreferenceChanges } from '../../api/notification-queries';
import type { NotificationPreferences } from '../../api/types';
import { lockedHint, noticeLines, noticeWords, type NoticeKind } from './notice-words';
import '../company/company.css';
import './notice-choices.css';

type Channel = 'inApp' | 'email';

/** The same choices with one channel of one kind flipped. */
export function withChoice(
  preferences: NotificationPreferences,
  kind: NoticeKind['kind'],
  change: Partial<Pick<NoticeKind, Channel>>,
): NotificationPreferences {
  return {
    ...preferences,
    kinds: preferences.kinds.map((k) => (k.kind === kind ? { ...k, ...change } : k)),
  };
}

/**
 * "Notices", in the profile's "Emails and notices" sheet: one row per kind of notice Paige sends this
 * person in this company, a switch for the bell and, where Paige also mails the kind to them, one for
 * the email. A flip shows at once and goes back if it did not save. A kind that cannot be turned off
 * is shown on, and says so.
 */
export function NoticeChoices({ tenantId }: { tenantId: string }): ReactNode {
  const choices = useNotificationPreferences(tenantId);
  const { save } = usePreferenceChanges(tenantId);
  const data = choices.data;
  const email = data?.kinds.some((k) => k.emailed) ?? false;
  const flip = (kind: NoticeKind['kind'], change: Partial<Pick<NoticeKind, Channel>>) => {
    // One change at a time: the next flip starts from what the last one saved.
    if (data && !save.isPending) save.mutate(withChoice(data, kind, change));
  };
  return (
    <section className="notice-choices" aria-labelledby="notice-choices-title">
      <header className="notice-choices__head">
        <h3 id="notice-choices-title">{noticeLines.title}</h3>
        {data ? <Columns email={email} /> : null}
      </header>
      {save.isError ? (
        <p className="sheet__line sheet__line--error" role="alert">
          {noticeLines.unsaved}
        </p>
      ) : null}
      {data ? (
        data.kinds.map((k) => (
          <NoticeRow key={k.kind} choice={k} emailColumn={email} onFlip={flip} />
        ))
      ) : (
        <p className="sheet__line">{choices.isError ? noticeLines.unread : noticeLines.reading}</p>
      )}
    </section>
  );
}

/** What each column of switches is, once, above them; the switches carry the same words for a screen reader. */
function Columns({ email }: { email: boolean }): ReactNode {
  return (
    <span className="notice-choices__cols" aria-hidden="true">
      <span>{noticeLines.bell}</span>
      {email ? <span>{noticeLines.email}</span> : null}
    </span>
  );
}

function NoticeRow({
  choice: k,
  emailColumn,
  onFlip,
}: {
  choice: NoticeKind;
  /** Some kind is mailed to this person: a kind that is not keeps its place empty, so the bells line up. */
  emailColumn: boolean;
  onFlip: (kind: NoticeKind['kind'], change: Partial<Pick<NoticeKind, Channel>>) => void;
}): ReactNode {
  const { icon: Icon, title, hint } = noticeWords(k.kind);
  const locked = k.locked === true;
  return (
    <div className={`notice-row${k.inApp || (k.emailed && k.email) ? ' is-on' : ''}`}>
      <Icon size={18} aria-hidden="true" />
      <span className="switch-row__copy">
        <span className="switch-row__title">{title}</span>
        <span className="switch-row__hint">
          {locked ? `${hint} ${lockedHint(k.kind)}`.trim() : hint}
        </span>
      </span>
      <NoticeSwitch
        label={`${title}: in Paige`}
        on={k.inApp}
        locked={locked}
        onFlip={(on) => onFlip(k.kind, { inApp: on })}
      />
      {k.emailed ? (
        <NoticeSwitch
          label={`${title}: by email`}
          on={k.email}
          locked={locked}
          onFlip={(on) => onFlip(k.kind, { email: on })}
        />
      ) : emailColumn ? (
        <span className="notice-switch__none" />
      ) : null}
    </div>
  );
}

/** One real switch with its own name: the row has two, so the row cannot be the label. */
function NoticeSwitch(props: {
  label: string;
  on: boolean;
  locked: boolean;
  onFlip: (on: boolean) => void;
}): ReactNode {
  return (
    <label className="notice-switch">
      <input
        type="checkbox"
        role="switch"
        aria-label={props.label}
        checked={props.on}
        disabled={props.locked}
        onChange={(e) => props.onFlip(e.currentTarget.checked)}
      />
      <span className="switch" aria-hidden="true" />
    </label>
  );
}
